import { trackPromise } from 'react-promise-tracker';

import { GetFileExtension, getJwt, imageFormats } from '@app/helpers';
import { OptionProps } from '@digital/ui';
import Axios from 'axios';

import api from './api';
import { Notifiable } from '@app/types/core.types';

export interface Usuario extends Notifiable {
  id?: string;
  email?: string;
  foto?: string;
  identificadorErp?: string;
  nome?: string;
  telefone1?: string;
  telefone2?: string;
  username?: string;
  tipoUsuario?: string;
  tipoUsuarioCodigo?: number;
  status: string;
  cargo?: string;
  perfis?: Array<string>;
  contrato?: string;
  fornecedor?: string;
  dataHoraUltimaAcesso?: Date;
  numero?: string
}

export interface CartaNomeacao {
  id: string;
  extensao: string;
  local: string;
  nome: string;
  responsavel: string;
  dataInclusao: string;
  numero: number;
  dataInclusaoFormatada: string;
}


export interface MostrarDiretoriaAreaDepartamento {
  diretoria: boolean;
  area: boolean;
  departamento: boolean;
}

export interface ContratoAcessivel {
  id: string;
  numero: number;
  objetoResumido: string;
  fornecedor: string;
  estaBloqueado: boolean;
}

export const getAllUsuarios = () => api.get<Usuario[]>('usuarios');
export const getAllUsuariosMRN = () => api.get<Usuario[]>('usuarios/mrn');
export const getAllUsuariosMRNAndTerceiro = () => api.get<Usuario[]>('usuarios/mrnandterceiro');
export const getAllUsuariosFornecedor = () => api.get<Usuario[]>('usuarios/fornecedor');
export const getAllUsuariosFornecedorCmg = () => api.get<Usuario[]>('usuarios/fornecedorCmg');
export const getAllGestores = () => api.get<Usuario[]>('usuarios/gestores');
export const getAllFiscais = () => api.get<Usuario[]>('usuarios/fiscais');
export const getAllAnalistaGPC = () => api.get<Usuario[]>('usuarios/analistagpc');
export const getAllAdministradoresContrato = () => api.get<Usuario[]>('usuarios/administradorescontrato');
export const getAllUsuarioResponsavelRateio = () => api.get<Usuario[]>('usuarios/getAllUsuarioResponsavelRateio');

export const getUsuarioLogadoInfo = () => api.get<Usuario>(`usuarios/getUsuarioLogado`);
export const getUsuarioInfo = (id: any) => api.get<Usuario>(`usuarios/${id}`);
export const getUsuarioPermissions = (id: any) => api.get<string[]>(`usuarios/getPermissions/${id}`);
export const getAllTecinicoMedicao = () => api.get<Usuario[]>('usuarios/tecnicoMedicao');

export const createUsuarioTerceiro = (usuario: Usuario) => {
  return api.post<Usuario>('usuarios/createUserTerceiro', usuario);
};
export const updateUsuario = (usuario: Usuario) => api.put(`usuarios/${usuario.id}`, usuario);
export const deleteUsuario = (id: string) => api.delete(`usuarios/${id}`);

export const getAllOptions = () =>
  getAllUsuarios().then(usuarios => (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })));

export const getAllGestoresOptions = () =>
  getAllGestores().then(usuarios => (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })));

export const getAllTenicoMedicaoOptions = () =>
  getAllTecinicoMedicao().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const getAllUsuariosMRNOptions = () =>
  getAllUsuariosMRN().then(usuarios => (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })));

export const getAllUsuariosMRNAndTerceiroOptions = () =>
  getAllUsuariosMRNAndTerceiro().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const getAllFornecedoresOptions = () =>
  getAllUsuariosFornecedor().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

 export const getAllFornecedoresWithNumberContratoOptions = () =>
  getAllUsuariosFornecedorCmg().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome + `${a.numero ? " - " + a.username: ""} ` , value: a.id && a.id.toString() })),
  );

export const getAllFiscaisOptions = () =>
  getAllFiscais().then(usuarios => (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })));

export const getAllAnalistaGPCOptions = () =>
  getAllAnalistaGPC().then(usuarios => (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })));

export const getAllAdministradoresContratoOptions = () =>
  getAllAdministradoresContrato().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const getAllUsuarioResponsavelRateioOptions = () =>
  getAllUsuarioResponsavelRateio().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const ativarUsuario = (usuarioId: Usuario) => api.put(`usuarios/ativarUsuario/${usuarioId}`);
export const inativarUsuario = (usuarioId: Usuario) => api.put(`usuarios/inativarUsuario/${usuarioId}`);
export const approveChanges = (usuarioId: Usuario) => api.put(`usuarios/aprovarAlteracoesUsuario/${usuarioId}`);
export const changePassword = (usuarioId: string, data: any) => api.put(`usuarios/changePassword/${usuarioId}`, data);
export const updatePrimeiroAcesso = (data: any) => api.put(`usuarios/updateFirstAccess/`, data);

export const getAllCartaNomeacaoPorUsuarioId = (id: string) => {
  return api.get<CartaNomeacao[]>(`usuarios/getAllCartaNomeacaoByUserId/${id}`);
};

export const verificarSeUsuarioSelecionadoEGestor = (id: string) => {
  return api.get<boolean>(`usuarios/verificarSeUsuarioSelecionadoEGestor/${id}`);
};
export const verificarSeUsuarioEPreSuprimento = () => {
  return api.get<boolean>(`usuarios/verificarSeUsuarioEPreSuprimento`);
};
export const verificarSeUsuarioAdministradorContrato = () => {
  return api.get<boolean>(`usuarios/verificarSeUsuarioAdministradorContrato`);
};

export const getContratosAcessiveis = (usuarioId: string) => {
  return api.get<ContratoAcessivel[]>(`usuarios/getContratosAcessiveis/${usuarioId}`);
};

export const saveContratosBloqueados = (usuarioId: string, contratoIds: string[]) => {
  return api.post<boolean>(`usuarios/saveContratosBloqueados`, {
    usuarioId,
    contratoIds
  });
};

export const getFileCartaNomeacaoById = (anexoId: string, nome: string) => {
  const urlBase = window.location.origin;
  trackPromise(
    Axios.get(urlBase + `/api/usuarios/getFileCartaNomeacaoById/${anexoId}`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }).then(resp => {
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nome);
      link.setAttribute('id', anexoId);
      document.body.appendChild(link);
      link.click();
      document.getElementById(anexoId).outerHTML = '';
    }),
  );
};

export const viewFileCartaNomeacaoById = (anexoId: string, nome: string) => {
  const urlBase = window.location.origin;
  trackPromise(
    Axios.get(urlBase + `/api/usuarios/getFileCartaNomeacaoById/${anexoId}`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${getJwt()}`,
      },
    }).then(response => {
      let file = undefined;
      let fileUrl = undefined;
      const fileExtension = GetFileExtension(nome);
      if (fileExtension === 'pdf') {
        file = new Blob([response.data], { type: 'application/pdf' });
        fileUrl = URL.createObjectURL(file);
      } else if (imageFormats.includes(fileExtension)) {
        file = new Blob([response.data], { type: 'image/' + fileExtension });
        fileUrl = URL.createObjectURL(file);
      }
      if (fileUrl !== undefined) var resp = window.open(fileUrl);
    }),
  );
};

export const excluirArquivoCartaNomeacao = (id: string) => api.delete(`usuarios/excluirArquivoCartaNomeacao/${id}`);

export const getDiretoriasResponsavel = () => api.get(`usuarios/getDiretoriasResponsavel`);
export const getAreasResponsavel = () => api.get(`usuarios/getAreasResponsavel`);

export const getAllGerenteAreaOptions = () =>
  getAllUsuariosGerenteAreas().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const getAllGerenteDepartamentoOptions = () =>
  getAllUsuariosGerenteDepartamento().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const getAllDiretorOptions = () =>
  getAllUsuariosDiretor().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const getAllUsuariosDiretor = () => api.get<Usuario[]>('usuarios/getAllUsuariosDiretor');
export const getAllUsuariosGerenteAreas = () => api.get<Usuario[]>('usuarios/getAllUsuariosGerenteAreas');
export const getAllUsuariosGerenteDepartamento = () => api.get<Usuario[]>('usuarios/getAllUsuariosGerenteDepartamento');

export const getAllUsuariosSuprimentos = () => api.get<Usuario[]>('usuarios/suprimentos');
export const getAllUsuariosSuprimentosptions = () =>
  getAllUsuariosSuprimentos().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const getAllUsuariosRequisitantes = () => api.get<Usuario[]>('usuarios/requisitantes');
export const getAllUsuariosRequisitantesOptions = () =>
  getAllUsuariosRequisitantes().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const getAllUsuariosByPerfilId = (id: string) => api.get<Usuario[]>(`usuarios/getAllUsuariosByPerfilId/${id}`);
export const getAllUsuariosByPerfilIdOptions = (id: string) =>
  getAllUsuariosByPerfilId(id).then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const getAllUsuariosPlanejamentoControleGASOptions = () =>
  getAllUsuariosPlanejamentoControleGAS().then(usuarios =>
    (usuarios || []).map(a => ({ text: a.nome, value: a.id && a.id.toString() })),
  );

export const getAllUsuariosPlanejamentoControleGAS = () => api.get<Usuario[]>('usuarios/getAllUsuariosPlanejamentoControleGAS');
